import { numFont } from '../../../utils/fonts';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, CircularProgress, IconButton, Link, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import LaunchIcon from '@mui/icons-material/Launch';
import RemoveIcon from '@mui/icons-material/Remove';
import ReplayIcon from '@mui/icons-material/Replay';
import CheckIcon from '@mui/icons-material/Check';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import ManageSearchOutlinedIcon from '@mui/icons-material/ManageSearchOutlined';
import WarehouseOutlinedIcon from '@mui/icons-material/WarehouseOutlined';
import { CARD_SX, CodeText, ExVatPrice, TONE, TagChip, VendorHeading } from './builderUi';

/** One source of the fan-out: "Rekisteri · 2" with a check, a spinner, or the failure. */
function SourceStatus({ label, state, t, onRetry, testId }) {
  const status = state?.status || 'idle';
  const count = state?.items?.length ?? state?.hits?.length ?? 0;
  let tone = TONE.slate;
  let icon = null;
  let text = label;
  if (status === 'loading') {
    icon = <CircularProgress size={12} sx={{ color: 'inherit' }} />;
  } else if (status === 'ok') {
    tone = count > 0 ? TONE.green : TONE.slate;
    icon = <CheckIcon />;
    text = `${label} · ${count}`;
  } else if (status === 'failed') {
    tone = TONE.red;
    icon = <ErrorOutlineIcon />;
    text = t('orderBuilder.sourceFailed', { source: label });
  } else if (status === 'skipped') {
    icon = <SearchOffIcon />;
    text = t('orderBuilder.sourceCodeOnly', { source: label });
  }
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }} data-testid={testId}>
      <TagChip label={text} tone={tone} icon={icon || undefined} sx={{ ...numFont }} />
      {status === 'failed' && onRetry && (
        <Button size="small" onClick={onRetry} sx={{ minWidth: 0, fontSize: 12, fontWeight: 600, py: 0, px: 0.75, textTransform: 'none' }}>
          {t('orderBuilder.retry')}
        </Button>
      )}
    </Box>
  );
}

function stockText(row, t) {
  const stock = row.stock;
  if (!stock) return null;
  if (stock.kind === 'live') {
    if (stock.total == null) return stock.estimate || null;
    const parts = [];
    parts.push(stock.total > 0 ? t('orderBuilder.stockLive', { count: stock.total }) : t('orderBuilder.stockLiveNone'));
    if (stock.deliveryDays != null) parts.push(t('orderBuilder.deliveryDays', { count: stock.deliveryDays }));
    if (stock.estimate) parts.push(stock.estimate);
    return parts.join(' · ');
  }
  if (stock.kind === 'catalogue') return t('orderBuilder.stockCatalogue', { count: stock.total });
  return null;
}

function ReasonText({ row, t, onRetry }) {
  const vendor = row.integration?.display_name;
  switch (row.reason) {
    case 'no_channel':
      return <Typography sx={{ fontSize: 12, color: 'text.disabled', mt: 0.5 }}>{t('orderBuilder.reason.noChannel')}</Typography>;
    case 'checking':
      return (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, mt: 0.5 }}>
          <CircularProgress size={12} />
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{t('orderBuilder.reason.checking', { vendor })}</Typography>
        </Box>
      );
    case 'unknown':
      return (
        <Typography sx={{ fontSize: 12, fontWeight: 500, color: TONE.amber.color, mt: 0.5 }}>
          {t('orderBuilder.reason.unknown', { vendor })}
        </Typography>
      );
    case 'failed':
      return (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 500, color: TONE.red.color }}>{t('orderBuilder.reason.failed', { vendor })}</Typography>
          {onRetry && (
            <IconButton size="small" onClick={onRetry} sx={{ p: 0.25, color: TONE.red.color }} aria-label={t('orderBuilder.retry')}>
              <ReplayIcon sx={{ fontSize: 15 }} />
            </IconButton>
          )}
        </Box>
      );
    default:
      return null;
  }
}

function ResultRow({ row, inCart, onAdd, onChangeQuantity, canViewPrices, t, onRetryVerify }) {
  const tags = [];
  if (row.live) tags.push({ key: 'live', label: t('orderBuilder.chip.live'), tone: TONE.accent });
  if (row.catalogue) tags.push({ key: 'cat', label: t('orderBuilder.chip.catalogue'), tone: TONE.teal });
  if (row.register) tags.push({ key: 'reg', label: t('orderBuilder.chip.register'), tone: TONE.green });
  if (row.matchedBy === 'oe' && row.oeNumber) tags.push({ key: 'oe', label: `OE ${row.oeNumber}`, tone: TONE.purple });
  const stock = stockText(row, t);
  const registerStock = row.registerStock ? t('orderBuilder.registerStock', { count: row.registerStock.quantity }) : null;
  const dim = !row.orderable && !inCart;

  return (
    <Box
      data-testid={`result-row-${row.rowKey}`}
      sx={{
        display: 'flex',
        gap: 1.5,
        alignItems: 'center',
        py: 1.5,
        px: 2.25,
        flexWrap: 'wrap',
        '& + &': { borderTop: '1px solid', borderColor: 'divider' },
        bgcolor: inCart ? 'rgba(49, 130, 206, 0.05)' : 'transparent',
        transition: 'background-color 150ms',
        '&:hover': { bgcolor: inCart ? 'rgba(49, 130, 206, 0.07)' : 'background.default' },
      }}
    >
      <Box sx={{ flex: '1 1 240px', minWidth: 0 }}>
        <Typography sx={{ fontSize: 14.5, fontWeight: 600, lineHeight: 1.3, color: dim ? 'text.secondary' : 'text.primary' }} noWrap title={row.name}>
          {row.name || row.code}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline', flexWrap: 'wrap', mt: 0.25 }}>
          {row.brand && <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{row.brand}</Typography>}
          <CodeText>{row.code}</CodeText>
          {row.ean && <CodeText sx={{ color: 'text.disabled', fontWeight: 400 }}>EAN {row.ean}</CodeText>}
        </Box>
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 0.75, alignItems: 'center' }}>
          {tags.map((tag) => (
            <TagChip key={tag.key} label={tag.label} tone={tag.tone} />
          ))}
          {(stock || registerStock) && (
            <Typography sx={{ fontSize: 12, color: 'text.secondary', display: 'inline-flex', alignItems: 'center', gap: 0.5, ...numFont }}>
              <WarehouseOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
              {[stock, registerStock].filter(Boolean).join(' · ')}
            </Typography>
          )}
        </Box>
        <ReasonText row={row} t={t} onRetry={onRetryVerify} />
      </Box>
      <Box sx={{ textAlign: 'right', flex: '0 0 auto', minWidth: 104 }}>
        {canViewPrices ? (
          <>
            <ExVatPrice value={row.purchasePrice} t={t} size={15} weight={700} testId={`result-price-${row.rowKey}`} />
            {row.listPrice != null && (
              <Typography sx={{ fontSize: 12, color: 'text.secondary', ...numFont }}>
                {t('orderBuilder.listPrice')} <ExVatPrice value={row.listPrice} t={t} size={12} weight={500} muted />
              </Typography>
            )}
          </>
        ) : (
          <Typography sx={{ fontSize: 15, color: 'text.disabled' }}>–</Typography>
        )}
      </Box>
      <Box sx={{ flex: '0 0 auto', display: 'flex', alignItems: 'center' }}>
        {inCart ? (
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              border: '1px solid',
              borderColor: TONE.accent.borderColor,
              bgcolor: 'background.paper',
              borderRadius: '9px',
              p: '2px',
            }}
          >
            <IconButton size="small" onClick={() => onChangeQuantity(-1)} aria-label={t('orderBuilder.decrease')} sx={{ color: 'secondary.main' }}>
              <RemoveIcon sx={{ fontSize: 16 }} />
            </IconButton>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: TONE.accent.color, minWidth: 70, textAlign: 'center', ...numFont }} data-testid={`in-cart-${row.rowKey}`}>
              {t('orderBuilder.inCart', { count: inCart.quantity })}
            </Typography>
            <IconButton size="small" onClick={() => onChangeQuantity(1)} aria-label={t('orderBuilder.increase')} sx={{ color: 'secondary.main' }}>
              <AddIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        ) : (
          <Button
            variant="contained"
            size="small"
            disabled={!row.orderable}
            onClick={onAdd}
            startIcon={<AddIcon />}
            data-testid={`add-${row.rowKey}`}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '9px', whiteSpace: 'nowrap', boxShadow: 'none', px: 1.75 }}
          >
            {t('orderBuilder.add')}
          </Button>
        )}
      </Box>
    </Box>
  );
}

function GroupCard({ group, children, t, aside, testId, name }) {
  return (
    <Box sx={{ ...CARD_SX, mt: 1.5 }} data-testid={testId}>
      <VendorHeading
        integration={group.integration}
        name={name}
        t={t}
        aside={aside}
        sx={{ px: 2.25, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}
      />
      {children}
    </Box>
  );
}

/**
 * Results of the fan-out search, grouped by vendor so the split into several orders is
 * visible from the first click.
 */
function SearchResultsPane({ searchState, cartByRow, onAdd, onChangeQuantity, canViewPrices, onRetryVerify }) {
  const { t } = useTranslation();
  const { active, register, catalogue, live, liveIntegrations, groups, retryLive, anyLoading, codeLike } = searchState;
  const [showUnorderable, setShowUnorderable] = useState(false);

  if (!active) {
    return (
      <Box sx={{ py: { xs: 4, md: 7 }, px: 3, textAlign: 'center' }} data-testid="search-empty-hint">
        <Box
          sx={{
            width: 52, height: 52, borderRadius: '14px', bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: 'text.secondary', mb: 1.5,
          }}
        >
          <ManageSearchOutlinedIcon sx={{ fontSize: 26 }} />
        </Box>
        <Typography sx={{ fontSize: 15, fontWeight: 600 }}>{t('orderBuilder.searchHint')}</Typography>
        {liveIntegrations.length > 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, lineHeight: 1.45 }}>
            {t('orderBuilder.searchLiveHint', { vendors: liveIntegrations.map((i) => i.display_name).join(', ') })}
          </Typography>
        )}
      </Box>
    );
  }

  const orderableGroups = groups.filter((g) => g.orderable);
  const otherGroups = groups.filter((g) => !g.orderable);
  const rowCount = (group) => (
    <Typography sx={{ fontSize: 12.5, color: 'text.secondary', ...numFont }}>{t('orderBuilder.rowCount', { count: group.rows.length })}</Typography>
  );

  return (
    <Box data-testid="search-results">
      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center' }} data-testid="source-status">
        <SourceStatus label={t('orderBuilder.sourceRegister')} state={register} t={t} testId="source-register" />
        <SourceStatus label={t('orderBuilder.sourceCatalogues')} state={catalogue} t={t} testId="source-catalogue" />
        {liveIntegrations.map((integration) => (
          <SourceStatus
            key={integration.id}
            label={integration.display_name}
            state={live[integration.id]}
            t={t}
            onRetry={() => retryLive(integration.id)}
            testId={`source-live-${integration.id}`}
          />
        ))}
      </Box>
      {catalogue.truncated && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
          {t('orderBuilder.truncated')}
        </Typography>
      )}
      {!anyLoading && groups.length === 0 && (
        <Alert severity="info" sx={{ mt: 1.5, borderRadius: '12px' }} data-testid="search-no-results">
          {codeLike ? t('orderBuilder.noResultsCode') : t('orderBuilder.noResultsName')}
        </Alert>
      )}
      {orderableGroups.map((group) => (
        <GroupCard key={group.key} group={group} name={group.vendorName} t={t} aside={rowCount(group)} testId={`result-group-${group.key}`}>
          {group.rows.map((row) => (
            <ResultRow
              key={row.rowKey}
              row={row}
              inCart={cartByRow[row.rowKey] || null}
              onAdd={() => onAdd(row)}
              onChangeQuantity={(delta) => onChangeQuantity(row, delta)}
              canViewPrices={canViewPrices}
              t={t}
              onRetryVerify={row.reason === 'failed' ? () => onRetryVerify(row) : null}
            />
          ))}
        </GroupCard>
      ))}
      {otherGroups.length > 0 && (
        <Box sx={{ mt: 1.5 }}>
          <Button
            size="small"
            onClick={() => setShowUnorderable((v) => !v)}
            sx={{ textTransform: 'none', fontWeight: 600, color: 'text.secondary', px: 0.75 }}
            data-testid="toggle-unorderable"
          >
            {showUnorderable
              ? t('orderBuilder.hideUnorderable')
              : t('orderBuilder.showUnorderable', { count: otherGroups.reduce((n, g) => n + g.rows.length, 0) })}
          </Button>
          {showUnorderable &&
            otherGroups.map((group) => (
              <GroupCard key={group.key} group={group} name={group.vendorName || t('orderBuilder.noVendor')} t={t} aside={rowCount(group)}>
                <Box
                  sx={{ px: 2.25, py: 1.25, bgcolor: TONE.amber.bgcolor, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', gap: 0.75, alignItems: 'flex-start' }}
                  data-testid={`no-channel-${group.key}`}
                >
                  <ErrorOutlineIcon sx={{ fontSize: 16, color: TONE.amber.color, mt: '2px', flexShrink: 0 }} />
                  <Typography sx={{ fontSize: 13, color: 'text.primary', lineHeight: 1.45 }}>
                    {t('orderBuilder.reason.noChannelHelp')}{' '}
                    {group.vendorId ? (
                      <Link
                        component={RouterLink}
                        to={`/vendors?edit=${group.vendorId}`}
                        sx={{ fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 0.5, whiteSpace: 'nowrap' }}
                        data-testid={`no-channel-link-${group.key}`}
                      >
                        {t('orderBuilder.reason.noChannelLink')}
                        <LaunchIcon sx={{ fontSize: 13 }} />
                      </Link>
                    ) : null}
                  </Typography>
                </Box>
                {group.rows.map((row) => (
                  <ResultRow key={row.rowKey} row={row} inCart={null} onAdd={() => {}} onChangeQuantity={() => {}} canViewPrices={canViewPrices} t={t} />
                ))}
              </GroupCard>
            ))}
        </Box>
      )}
    </Box>
  );
}

export default SearchResultsPane;
